import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import {
  type CommunicationStateRow,
  useCommunicationState,
  useCommunicationStats,
} from "@features/communications/api";
import { useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import EnterpriseTable from "@shared/ui/enterprise-table";
import {
  Alert,
  Card,
  Col,
  InputNumber,
  Row,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

const connectionOptions = [
  { label: "Все", value: "all" },
  { label: "Есть связь", value: "online" },
  { label: "Нет связи", value: "offline" },
];

const CommunicationStatePage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects, { required: false });
  const [placeId, setPlaceId] = useState<number | undefined>();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const statsQuery = useCommunicationStats({
    region: selectedObject?.regionId,
    object: selectedObject?.objectId,
  });

  const stateQuery = useCommunicationState({
    region: selectedObject?.regionId,
    object: selectedObject?.objectId,
    place: placeId,
    limit: 500,
  });

  const data = useMemo(() => {
    if (!stateQuery.data) {
      return [];
    }
    return stateQuery.data.filter((row) => {
      if (statusFilter === "online") {
        return row.connection_state === 1;
      }
      if (statusFilter === "offline") {
        return row.connection_state === 0;
      }
      return true;
    });
  }, [stateQuery.data, statusFilter]);

  const columns: ColumnsType<CommunicationStateRow> = [
    {
      title: "Счётчик",
      dataIndex: "meter_id",
      width: 140,
      render: (_value, row) => row.meter_id ?? "—",
    },
    {
      title: "Серийный номер",
      dataIndex: "serial_number",
      width: 180,
      render: (value?: string) => value ?? "—",
    },
    {
      title: "Объект",
      dataIndex: "object_name",
      width: 220,
      render: (_value, row) =>
        row.object_name ? `${row.object_name} (№${row.object_id ?? "?"})` : "—",
    },
    {
      title: "Фидер",
      dataIndex: "feeder_name",
      width: 160,
      render: (_value, row) =>
        row.feeder_name ? `${row.feeder_name} (№${row.feeder_id ?? "?"})` : "—",
    },
    {
      title: "Канал связи",
      dataIndex: "channel_name",
      width: 220,
      render: (_value, row) => row.channel_name ?? row.port_name ?? "—",
    },
    {
      title: "Телефон / IP",
      dataIndex: "host",
      width: 220,
      render: (_value, row) => {
        if (row.host && row.tcp_port) {
          return `${row.host}:${row.tcp_port}`;
        }
        if (row.host) {
          return row.host;
        }
        return row.phone ?? "—";
      },
    },
    {
      title: "Связь",
      dataIndex: "connection_state",
      width: 150,
      render: (_value, row) => renderStatus(row),
    },
    {
      title: "Последний опрос",
      dataIndex: "last_poll_at",
      width: 180,
      render: (value?: string) => (value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "—"),
    },
    {
      title: "Расхождение времени, сек",
      dataIndex: "time_delta",
      width: 200,
      render: (_value, row) => renderDelta(row),
    },
    {
      title: "Ошибки",
      dataIndex: "comm_errors",
      width: 140,
      render: (_value, row) => renderErrors(row),
    },
  ];

  const stats = statsQuery.data;

  return (
    <PageLayoutWithHelp title="Статус коммуникаций">
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          Мониторинг каналов связи и счётчиков: наличие связи, последние опросы, расхождение времени
          и ошибки опроса.
        </Typography.Text>

        <Row gutter={16}>
          <Col span={6}>
            <Card size="small" loading={statsQuery.isLoading}>
              <Statistic
                title="Онлайн"
                value={stats?.online ?? 0}
                styles={{ content: { color: "#52c41a" } }}
                prefix="🟢"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" loading={statsQuery.isLoading}>
              <Statistic
                title="Опрос"
                value={stats?.polling ?? 0}
                styles={{ content: { color: "#faad14" } }}
                prefix="🟡"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" loading={statsQuery.isLoading}>
              <Statistic
                title="Оффлайн"
                value={stats?.offline ?? 0}
                styles={{ content: { color: "#ff4d4f" } }}
                prefix="🔴"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" loading={statsQuery.isLoading}>
              <Statistic
                title="С ошибками"
                value={stats?.errors ?? 0}
                styles={{ content: { color: "#722ed1" } }}
                prefix="⚠️"
              />
            </Card>
          </Col>
        </Row>

        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              allowClear
              placeholder="Объект (все доступные)"
              style={{ minWidth: 320 }}
            />
            <InputNumber
              value={placeId}
              onChange={(value) => setPlaceId(value ?? undefined)}
              placeholder="ID канала"
              style={{ width: 160 }}
            />
            <Select
              options={connectionOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value)}
              style={{ width: 200 }}
            />
          </FilterRow>
        </FilterBar>

        {!stateQuery.isLoading && data.length === 0 ? (
          <Alert
            type="info"
            showIcon
            title="Данные отсутствуют"
            description="Попробуйте изменить фильтры или убедиться, что выбраны корректные объекты."
          />
        ) : (
          <EnterpriseTable<CommunicationStateRow>
            rowKey={(_row, index) =>
              `${_row.meter_id ?? ""}-${_row.place_id ?? ""}-${_row.object_id ?? ""}-${_row.feeder_id ?? ""}-${index}`
            }
            columns={columns}
            dataSource={data}
            loading={stateQuery.isLoading}
            scroll={{ x: 1200 }}
            size="small"
            toolbar={{ reload: true, onReload: () => stateQuery.refetch() }}
          />
        )}
      </Space>
    </PageLayoutWithHelp>
  );
};

const renderStatus = (row: CommunicationStateRow) => {
  if (row.connection_state === 1) {
    return (
      <Tag color="green">
        есть с {row.connection_since ? dayjs(row.connection_since).format("DD.MM HH:mm") : "—"}
      </Tag>
    );
  }
  if (row.connection_state === 0) {
    return (
      <Tag color="red">
        нет с {row.connection_since ? dayjs(row.connection_since).format("DD.MM HH:mm") : "—"}
      </Tag>
    );
  }
  return <Tag>неизвестно</Tag>;
};

const renderDelta = (row: CommunicationStateRow) => {
  if (row.time_delta === undefined || row.time_delta === null) {
    return "—";
  }
  const color =
    Math.abs(row.time_delta) >= 25 ? "red" : Math.abs(row.time_delta) >= 5 ? "orange" : "default";
  return (
    <Tag color={color}>
      {row.time_delta.toFixed(0)} (от{" "}
      {row.time_delta_at ? dayjs(row.time_delta_at).format("DD.MM HH:mm") : "—"})
    </Tag>
  );
};

const renderErrors = (row: CommunicationStateRow) => {
  const comm = row.comm_errors ?? 0;
  const proto = row.protocol_errors ?? 0;
  if (comm === 0 && proto === 0 && !row.sys_error) {
    return <Tag color="green">нет</Tag>;
  }
  return (
    <Space orientation="vertical" size={0}>
      {comm ? <Tag color="red">Связь: {comm}</Tag> : null}
      {proto ? <Tag color="orange">Протокол: {proto}</Tag> : null}
      {row.sys_error ? <Tag color="purple">Код {row.sys_error}</Tag> : null}
    </Space>
  );
};

export default CommunicationStatePage;
